import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { categoryIcons } from "@/lib/icons";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useDragScroll } from "@/hooks/useDragScroll";
import { ScrollHint } from "@/components/ui/scroll-hint";

interface Category {
  id: string;
  name: string;
  description?: string;
  image: string;
  slug: string;
}

interface CategoryTab {
  id: string;
  label: string;
  categories: Category[];
}

interface CategoryTabsProps {
  title: string;
  tabs: CategoryTab[];
}

export const CategoryTabs = ({ title, tabs }: CategoryTabsProps) => {
  const [activeTab, setActiveTab] = useState(tabs[0]?.id || "");
  const [canScroll, setCanScroll] = useState(false);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(true);
  
  const { 
    scrollRef, 
    isDragging,
    handleMouseDown, 
    handleMouseMove, 
    handleMouseUp, 
    handleMouseLeave 
  } = useDragScroll();

  const activeCategories = tabs.find(t => t.id === activeTab)?.categories || [];

  const checkScroll = () => {
    const container = scrollRef.current;
    if (container) {
      const hasScroll = container.scrollWidth > container.clientWidth;
      setCanScroll(hasScroll);
      setShowLeftGradient(container.scrollLeft > 10);
      setShowRightGradient(container.scrollLeft < container.scrollWidth - container.clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [activeCategories]);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollRef.current;
    if (container) {
      container.scrollBy({ left: direction === 'left' ? -300 : 300, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-10 lg:py-16 border-y border-border bg-secondary/30">
      <div className="max-w-[1600px] mx-auto px-3 lg:px-6">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-6 lg:mb-8">
          <p className="text-primary text-xs lg:text-sm font-bold uppercase tracking-widest mb-3 lg:mb-4">
            NAKUPOVAŤ PODĽA
          </p>
          <h2 className="text-2xl lg:text-4xl font-bold text-foreground mb-3 lg:mb-4">
            Nakupovať <span className="bg-gradient-to-r from-primary via-[hsl(170,70%,35%)] to-[hsl(170,60%,45%)] bg-clip-text text-transparent">podľa</span>
          </h2>
          <p className="text-muted-foreground text-xs lg:text-lg max-w-2xl mx-auto px-4">
            Prezrite si naše produkty podľa kategórií, kolekcií alebo špeciálnych ponúk.
          </p>
        </div>
        
        {/* Large Centered Tabs - scrollable on mobile */}
        <div className="flex flex-col items-center gap-3 lg:gap-4 mb-6 lg:mb-10">
          <div className="w-full overflow-x-auto scrollbar-hide -mx-3 px-3 lg:mx-0 lg:px-0">
            <div className="flex justify-start lg:justify-center">
              <div className="inline-flex items-center gap-1 lg:gap-2 bg-secondary p-1 lg:p-2 rounded-xl lg:rounded-2xl shrink-0">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      "flex items-center gap-1 lg:gap-2 px-3 lg:px-6 py-2 lg:py-3 rounded-lg lg:rounded-xl text-xs lg:text-base font-bold transition-all whitespace-nowrap shrink-0",
                      activeTab === tab.id
                        ? "bg-foreground text-background shadow-lg"
                        : "text-muted-foreground hover:text-foreground hover:bg-background/50"
                    )}
                  >
                    {categoryIcons[tab.id] && (() => {
                      const Icon = categoryIcons[tab.id];
                      return <Icon className="w-4 h-4 lg:w-5 lg:h-5" />;
                    })()}
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          {canScroll && <ScrollHint show={showScrollHint} />}
        </div>

        {/* Categories - Horizontal scroll with arrows outside */}
        <div className="relative">
          {/* Scroll arrows - only show if scrollable, positioned outside content */}
          {canScroll && (
            <>
              <button 
                onClick={() => scroll('left')}
                className={cn(
                  "absolute -left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full border border-border bg-background hover:bg-secondary flex items-center justify-center transition-all shadow-lg",
                  !showLeftGradient && "opacity-50 pointer-events-none"
                )}
              >
                <ChevronLeft className="w-5 h-5 text-foreground" />
              </button>
              <button 
                onClick={() => scroll('right')}
                className={cn(
                  "absolute -right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full border border-border bg-background hover:bg-secondary flex items-center justify-center transition-all shadow-lg",
                  !showRightGradient && "opacity-50 pointer-events-none"
                )}
              >
                <ChevronRight className="w-5 h-5 text-foreground" />
              </button>
            </>
          )}

          {/* Gradient overlays - only show if scrollable */}
          {canScroll && showLeftGradient && (
            <div className="absolute left-0 top-0 bottom-4 w-16 bg-gradient-to-r from-secondary/80 to-transparent z-10 pointer-events-none" />
          )}
          {canScroll && showRightGradient && (
            <div className="absolute right-0 top-0 bottom-4 w-16 bg-gradient-to-l from-secondary/80 to-transparent z-10 pointer-events-none" />
          )}

          {/* Scrollable container with drag support */}
          <div 
            ref={scrollRef}
            onScroll={() => { checkScroll(); setShowScrollHint(false); }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            className={cn(
              "flex gap-4 overflow-x-auto pb-4",
              isDragging ? "cursor-grabbing" : "cursor-grab",
              !isDragging && "scroll-smooth"
            )}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {/* Center wrapper - uses justify-center when items fit, otherwise scrolls */}
            <div className={cn(
              "flex gap-4",
              !canScroll && "mx-auto"
            )}>
              {activeCategories.map((category) => (
                <Link
                  key={category.id}
                  to={`/kategoria/${category.slug}`}
                  className="group flex-shrink-0 w-48"
                >
                  <div className="relative h-40 rounded-xl overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-base font-bold text-background">{category.name}</h3>
                      {category.description && (
                        <p className="text-sm text-background/70 truncate">{category.description}</p>
                      )}
                    </div>
                    <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-background/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRight className="w-4 h-4 text-background" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
